import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Code, Link, Text } from '@customerio/pluma-components/react';
import { generatePath } from '@/services/router/generate-path';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { ACTIVITY_LOG_SOURCE } from '@/utils/activity-log-source-types';
import { parsePipelinesUserAgent } from '@/utils/pipelines-user-agent';
import { useActivityLogForms, useActivityLogSqlImport, useInAppFormParent } from '../hooks';
import type { ActivityLog, ActivityLogSource } from '../types';

/**
 * Human description of a single activity-log source, for the common payload-derivable source types.
 *
 * Cross-environment / journey-link nuances are simplified. Form links render as raw URLs because
 * their route isn't in the typed route table yet.
 */

/** form_type wire value → human label. */
const FORM_TYPE_LABELS: Record<string, string> = {
  facebook_lead_ads: 'Facebook Lead Ads',
  custom: 'Custom',
  typeform: 'Typeform',
  jotform: 'Jotform',
  in_app_form: 'In-App',
};
function formTypeLabel(formType: string | null | undefined): string {
  return FORM_TYPE_LABELS[formType ?? ''] ?? 'Custom';
}
/** Form-details link — mirrors the command-dialog mapper's `integrations/forms/:type/:id` URL. */
function formDetailsHref(workspaceId: string, formType: string, id: string | number): string {
  const urlType = formType === FACEBOOK_LEAD_ADS ? 'facebook' : formType;
  // oxlint-disable-next-line local-rules/enforce-generate-path -- integrations/forms route isn't in the typed route table
  return `/workspaces/${workspaceId}/journeys/integrations/forms/${urlType}/${id}`;
}

/** SQL-sync detail link (`integrations/sql-sync/:type/:id`). */
function sqlImportHref(workspaceId: string, databaseType: string, id: string | number): string {
  return generatePath('/workspaces/:workspaceId/journeys/integrations/sql-sync/:type/:sqlImportId', {
    workspaceId,
    type: databaseType,
    sqlImportId: id,
  });
}

const FACEBOOK_LEAD_ADS = 'facebook_lead_ads';
const IN_APP_FORM = 'in_app_form';

const SDK_CLIENT_TYPES: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  nse: 'Notification Service Extension',
};

type InlineCodeProps = { children: ReactNode; className?: string };

function InlineCode({ children, className }: InlineCodeProps) {
  return (
    <Code size="xxs" className={className}>
      {children}
    </Code>
  );
}

type TeamMemberLinkProps = { email: string | null | undefined };

/** The acting team member's email, linked to team settings. */
function TeamMemberLink({ email }: TeamMemberLinkProps) {
  // /settings/team is TanStack-owned, so its href comes from the router
  // (`generatePath` is restricted to the manual legacy routes).
  const router = useRouter();
  return (
    <Link href={router.buildLocation({ to: '/settings/team' }).href} variant="secondary" className="fs-mask">
      {email}
    </Link>
  );
}

function campaignHref(workspaceId: string, campaignId: number | null | undefined): string {
  return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
    workspaceId,
    campaignId: campaignId ?? '',
  });
}

type CampaignClauseProps = {
  source: ActivityLogSource;
  workspaceId: string;
  campaignSingular: string;
};

function CampaignClause({ source, workspaceId, campaignSingular }: CampaignClauseProps) {
  if (isEmptyString(source.campaign_name ?? '')) {
    return <>in a deleted {campaignSingular}</>;
  }
  return (
    <>
      in the <Link href={campaignHref(workspaceId, source.campaign_id)}>"{source.campaign_name}"</Link>{' '}
      {campaignSingular}
    </>
  );
}

type FormIntegrationClauseProps = {
  source: ActivityLogSource;
  workspaceId: string;
  forms: ReturnType<typeof useActivityLogForms>;
};

/**
 * `form_integration` source (non-in-app) — the submitted form as a named link. Facebook lead-ads
 * and custom/typeform/jotform forms are matched from the fetched form lists; an unresolved form
 * falls back to "{label} form with Form ID of {id}".
 * (In-app forms are handled separately by `InAppFormClause`.)
 */
function FormIntegrationClause({ source, workspaceId, forms }: FormIntegrationClauseProps) {
  const formType = source.form_type ?? '';
  const formId = source.form_id ?? '';
  const apiSuffix = source.api === 'js' ? ' via JS snippet' : source.api === 'REST' ? ' via API endpoint' : '';

  if (formType === FACEBOOK_LEAD_ADS) {
    const fb = forms.facebookForms.find((f) => f.facebook_form_id === formId);
    if (fb != null) {
      return (
        <Link href={formDetailsHref(workspaceId, FACEBOOK_LEAD_ADS, fb.id)} variant="secondary">
          {fb.facebook_form_name} Facebook Lead Ads form
        </Link>
      );
    }
    return <>Facebook Lead Ads form with Facebook Form ID of {formId}</>;
  }

  const form = forms.customForms.find((f) => f.form_type === formType && f.form_id === formId);
  const label = formTypeLabel(formType);
  if (form != null) {
    return (
      <>
        <Link href={formDetailsHref(workspaceId, formType, form.id)} variant="secondary">
          {form.name} {label} form
        </Link>
        {apiSuffix}
      </>
    );
  }
  return (
    <>
      {label} form with Form ID of {formId}
      {apiSuffix}
    </>
  );
}

function formName(log: ActivityLog): string | undefined {
  if (!isRecord(log.attrs)) return undefined;
  const name = log.attrs.form_name;
  return typeof name === 'string' && !isEmptyString(name) ? name : undefined;
}

type InAppFormClauseProps = {
  log: ActivityLog;
  source: ActivityLogSource;
  workspaceId: string;
  campaignSingular: string;
};

/**
 * In-app form source: the form name + a link to the parent campaign /
 * broadcast's form-submissions page. The parent is resolved via a dependent-fetch chain
 * (`useInAppFormParent`: template → action → campaign, or template → newsletter).
 */
function InAppFormClause({ log, source, workspaceId, campaignSingular }: InAppFormClauseProps) {
  const { parent, isLoading, hasTemplate } = useInAppFormParent(workspaceId, source.template_id);
  const name = formName(log);
  const nameNode =
    name != null ? (
      <Text as="span" weight="bold">
        {name}
      </Text>
    ) : (
      <>In-App form</>
    );

  if (parent != null) {
    const href =
      parent.kind === 'campaign'
        ? generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/form-submissions', {
            workspaceId,
            campaignId: parent.id,
          })
        : generatePath(
            '/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId/overview/form-submissions',
            {
              workspaceId,
              newsletterId: parent.id,
            },
          );
    const term = parent.kind === 'campaign' ? campaignSingular : 'One-time send';
    return (
      <>
        {nameNode} in the{' '}
        <Link href={href} variant="secondary" isUnderlined>
          "{parent.name}"
        </Link>{' '}
        {term}
      </>
    );
  }
  if (isLoading) return <>{nameNode} (loading…)</>;
  if (hasTemplate) return <>{nameNode} from unknown template</>;
  return nameNode;
}

type SqlImportClauseProps = { source: ActivityLogSource; workspaceId: string };

/**
 * SQL-import source: lazily resolves the import → "{name} SQL Sync"
 * link, or "SQL Sync with ID {id}" while loading / when the record is gone.
 */
function SqlImportClause({ source, workspaceId }: SqlImportClauseProps) {
  const { sqlImport, databaseType } = useActivityLogSqlImport(workspaceId, source.import_id);
  if (sqlImport != null && !isEmptyString(sqlImport.name ?? '')) {
    return (
      <Link href={sqlImportHref(workspaceId, databaseType, source.import_id ?? '')} variant="secondary">
        {sqlImport.name} SQL Sync
      </Link>
    );
  }
  return <>SQL Sync with ID {source.import_id}</>;
}

/**
 * Shared props for every source-type clause. Each clause destructures only what it needs; the
 * dispatch table (`SOURCE_RENDERERS`) keys the wire `type` to one of these so `FormattedLogSource`
 * stays a lookup instead of a 20-branch ladder.
 */
type SourceClauseProps = {
  type: string;
  log: ActivityLog;
  source: ActivityLogSource;
  workspaceId: string;
  campaignSingular: string;
  forms: ReturnType<typeof useActivityLogForms>;
};

function ListUnsubscribeClause() {
  return <>user unsubscription via List-Unsubscribe header</>;
}

type SourceOnlyProps = { source: ActivityLogSource };

function SdkClientClause({ source }: SourceOnlyProps) {
  const device = SDK_CLIENT_TYPES[(source.client?.type ?? '').toLowerCase()] ?? '';
  return (
    <>
      Customer.io {device} SDK {source.client?.version ?? ''}
    </>
  );
}

/**
 * Data Pipelines source. When the UA's trailing JSON carries the originating workspace/source, the
 * name links back to that pipeline source — matching the Ember component this page replaced.
 */
function DataPipelinesClause({ source }: SourceOnlyProps) {
  const router = useRouter();
  const { pipelinesUserAgent, userAgent, sourceName, sourceId, workspaceId } = parsePipelinesUserAgent(source.ua);
  const uaNode = <InlineCode>{userAgent ?? pipelinesUserAgent}</InlineCode>;

  if (isEmptyString(workspaceId) || isEmptyString(sourceId)) {
    return <>Customer.io Data Pipelines, user agent: {uaNode}</>;
  }

  const href = router.buildLocation({
    to: '/workspaces/$workspaceId/pipelines/sources/$sourceId/data-in',
    params: { workspaceId, sourceId },
  }).href;

  return (
    <>
      Customer.io Data Pipelines via{' '}
      <Link href={href} variant="secondary">
        {isEmptyString(sourceName) ? 'this source' : sourceName}
      </Link>
      , user agent: {uaNode}
    </>
  );
}

type SiteIdSuffixProps = { siteId: ActivityLogSource['site_id'] };

function SiteIdSuffix({ siteId }: SiteIdSuffixProps) {
  if (siteId == null) return null;
  return (
    <>
      , Site ID:{' '}
      <Code size="xxs" className="fs-exclude">
        {siteId}
      </Code>
    </>
  );
}

/** REST / JS-snippet track sources (with the optional Site ID); other apis show the bare label. */
function TrackApiHttpClause({ source }: SourceOnlyProps) {
  if (source.api !== 'REST' && source.api !== 'js') return <>Customer.io track API</>;
  const label = source.api === 'REST' ? 'Customer.io REST API' : 'Customer.io JavaScript tracking snippet';
  // The user agent and IP belong to the end user for a JS/REST track source — keep them out of
  // FullStory session replay.
  return (
    <>
      {label}, user agent: <InlineCode className="fs-mask">{source.ua}</InlineCode>, IP address:{' '}
      <InlineCode className="fs-mask">{source.ip_addr}</InlineCode>
      <SiteIdSuffix siteId={source.site_id} />
    </>
  );
}

function TrackApiClause({ source }: SourceClauseProps) {
  if (SDK_CLIENT_TYPES[(source.client?.type ?? '').toLowerCase()] != null) return <SdkClientClause source={source} />;
  if (isDataPipelinesSource(source)) {
    return <DataPipelinesClause source={source} />;
  }
  return <TrackApiHttpClause source={source} />;
}

function SubscriptionsClause() {
  return <>Customer.io mailing list subscription</>;
}

function WebhookClause({ source, workspaceId, campaignSingular }: SourceClauseProps) {
  return (
    <>
      webhook action <CampaignClause source={source} workspaceId={workspaceId} campaignSingular={campaignSingular} />
    </>
  );
}

function actionFallbackLabel(type: string): string {
  if (type === ACTIVITY_LOG_SOURCE.AttributeUpdateAction) return 'attribute update action';
  if (type === ACTIVITY_LOG_SOURCE.QueryCollection) return 'collection query action';
  if (type === ACTIVITY_LOG_SOURCE.QueryObjects) return 'object query action';
  return 'audience filter';
}

function sourceHasAction(source: ActivityLogSource): boolean {
  return (
    !isEmptyString(source.action_type ?? '') || !isEmptyString(source.action_name ?? '') || source.action_id != null
  );
}

/** The quoted action name when the source has one, else the type's generic fallback label. */
function actionDisplayName(source: ActivityLogSource, fallback: string): string {
  return sourceHasAction(source) && !isEmptyString(source.action_name ?? '') ? `"${source.action_name}"` : fallback;
}

function AttributeActionClause({ type, source, workspaceId, campaignSingular }: SourceClauseProps) {
  return (
    <>
      {actionDisplayName(source, actionFallbackLabel(type))}{' '}
      <CampaignClause source={source} workspaceId={workspaceId} campaignSingular={campaignSingular} />
    </>
  );
}

function ManualUiClause({ type, source }: SourceClauseProps) {
  const verb = type === ACTIVITY_LOG_SOURCE.UI ? 'manual change in dashboard by' : 'manual event send by';
  return (
    <>
      {verb} <TeamMemberLink email={source.email} />, IP address: <InlineCode>{source.ip_addr}</InlineCode>
    </>
  );
}

function ImportClause({ source, workspaceId }: SourceClauseProps) {
  return (
    <>
      <Link
        href={generatePath('/workspaces/:workspaceId/journeys/imports/:importId', {
          workspaceId,
          importId: source.import_id ?? '',
        })}
        variant="secondary"
      >
        CSV data import
      </Link>{' '}
      by <TeamMemberLink email={source.email} />
    </>
  );
}

function ActionClause({ source, workspaceId, campaignSingular }: SourceClauseProps) {
  const name = !isEmptyString(source.action_name ?? '') ? `"${source.action_name}"` : 'action';
  return (
    <>
      {name} action <CampaignClause source={source} workspaceId={workspaceId} campaignSingular={campaignSingular} />
    </>
  );
}

function NewsletterClause({ source }: SourceClauseProps) {
  return <>Broadcast{isEmptyString(source.newsletter_name ?? '') ? '' : `: ${source.newsletter_name}`}</>;
}

function TransactionalClause({ source }: SourceClauseProps) {
  return (
    <>
      Transactional message
      {isEmptyString(source.transactional_message_name ?? '') ? '' : `: ${source.transactional_message_name}`}
    </>
  );
}

function ContentClause({ source }: SourceClauseProps) {
  return <>{isEmptyString(source.content_name ?? '') ? 'Content' : source.content_name}</>;
}

/**
 * Data Pipelines auto-track sources tag their user agent with a
 * "Customer.io Data Pipelines" prefix (followed by an optional JSON metadata
 * blob). Shared by TrackApiClause and ProfileMergeClause.
 */
function isDataPipelinesSource(source: ActivityLogSource): boolean {
  return (source.ua ?? '').toLowerCase().startsWith('customer.io data pipelines');
}

/**
 * A profile merge triggered by a Data Pipelines auto-track call. Kept separate from
 * `DataPipelinesClause` rather than nesting it: Ember's ProfileMerge template wraps the source link
 * in parens ("via Customer.io Data Pipelines (SourceName)"), not the "via SourceName" phrasing
 * `DataPipelinesClause` uses standalone — nesting the two would double up "via".
 */
function ProfileMergeDataPipelinesClause({ source }: SourceOnlyProps) {
  const router = useRouter();
  const { pipelinesUserAgent, userAgent, sourceName, sourceId, workspaceId } = parsePipelinesUserAgent(source.ua);
  const uaNode = <InlineCode>{userAgent ?? pipelinesUserAgent}</InlineCode>;

  if (isEmptyString(workspaceId) || isEmptyString(sourceId)) {
    return <>Profile merge via Customer.io Data Pipelines, user agent: {uaNode}</>;
  }

  const href = router.buildLocation({
    to: '/workspaces/$workspaceId/pipelines/sources/$sourceId/data-in',
    params: { workspaceId, sourceId },
  }).href;

  return (
    <>
      Profile merge via Customer.io Data Pipelines (
      <Link href={href} variant="secondary">
        {isEmptyString(sourceName) ? 'this source' : sourceName}
      </Link>
      ), user agent: {uaNode}
    </>
  );
}

function ProfileMergeClause({ source }: SourceClauseProps) {
  if (!isEmptyString(source.email ?? '')) {
    return (
      <>
        Profile merge via UI by <TeamMemberLink email={source.email} />
      </>
    );
  }
  if (isDataPipelinesSource(source)) {
    return <ProfileMergeDataPipelinesClause source={source} />;
  }
  // A merge triggered via the API (identified by the presence of a Site ID).
  // The user agent and IP belong to the end user, so keep them out of
  // FullStory session replay.
  if (source.site_id != null) {
    return (
      <>
        Profile merge via API, user agent: <InlineCode className="fs-mask">{source.ua}</InlineCode>, IP address:{' '}
        <InlineCode className="fs-mask">{source.ip_addr}</InlineCode>
        <SiteIdSuffix siteId={source.site_id} />
      </>
    );
  }
  return <>Profile merge</>;
}

function SystemClause() {
  return <>Customer.io internal system</>;
}

function SystemAutoDeleteClause({ source }: SourceClauseProps) {
  return <>Customer.io Automated Device Clean-up Reason: {source.subtype}</>;
}

function FormIntegrationDispatch({ log, source, workspaceId, campaignSingular, forms }: SourceClauseProps) {
  if (source.form_type === IN_APP_FORM) {
    return <InAppFormClause log={log} source={source} workspaceId={workspaceId} campaignSingular={campaignSingular} />;
  }
  return <FormIntegrationClause source={source} workspaceId={workspaceId} forms={forms} />;
}

function SqlImportSourceClause({ source, workspaceId }: SourceClauseProps) {
  return <SqlImportClause source={source} workspaceId={workspaceId} />;
}

// Internal dev tool — only appears in internal/dev workspaces, so a friendly label beats a blank
// for the one audience (us) who sees it.
function DebugWidgetClause() {
  return <>Generated via developer tools</>;
}

/** Wire `source.type` → the clause that renders it. Types that share a clause map to the same one. */
const SOURCE_RENDERERS: Record<string, React.ComponentType<SourceClauseProps>> = {
  [ACTIVITY_LOG_SOURCE.ListUnsubscribe]: ListUnsubscribeClause,
  [ACTIVITY_LOG_SOURCE.TrackAPI]: TrackApiClause,
  [ACTIVITY_LOG_SOURCE.TrackEUAPI]: TrackApiClause,
  [ACTIVITY_LOG_SOURCE.Subscriptions]: SubscriptionsClause,
  [ACTIVITY_LOG_SOURCE.Webhook]: WebhookClause,
  [ACTIVITY_LOG_SOURCE.AttributeUpdateAction]: AttributeActionClause,
  [ACTIVITY_LOG_SOURCE.QueryCollection]: AttributeActionClause,
  // The step a failed_query_objects log came from.
  [ACTIVITY_LOG_SOURCE.QueryObjects]: AttributeActionClause,
  [ACTIVITY_LOG_SOURCE.ObjectCampaignsAudience]: AttributeActionClause,
  [ACTIVITY_LOG_SOURCE.UI]: ManualUiClause,
  [ACTIVITY_LOG_SOURCE.UIEvent]: ManualUiClause,
  [ACTIVITY_LOG_SOURCE.Import]: ImportClause,
  [ACTIVITY_LOG_SOURCE.Action]: ActionClause,
  [ACTIVITY_LOG_SOURCE.LLMAction]: ActionClause,
  [ACTIVITY_LOG_SOURCE.Newsletter]: NewsletterClause,
  [ACTIVITY_LOG_SOURCE.Transactional]: TransactionalClause,
  [ACTIVITY_LOG_SOURCE.Content]: ContentClause,
  [ACTIVITY_LOG_SOURCE.ProfileMerge]: ProfileMergeClause,
  [ACTIVITY_LOG_SOURCE.System]: SystemClause,
  [ACTIVITY_LOG_SOURCE.SystemAutomaticDeletion]: SystemAutoDeleteClause,
  [ACTIVITY_LOG_SOURCE.FormIntegration]: FormIntegrationDispatch,
  [ACTIVITY_LOG_SOURCE.SQLImport]: SqlImportSourceClause,
  [ACTIVITY_LOG_SOURCE.DebugWidget]: DebugWidgetClause,
};

type FormattedLogSourceProps = {
  log: ActivityLog;
  source: ActivityLogSource;
  workspaceId: string;
  campaignSingular: string;
};

export function FormattedLogSource({ log, source, workspaceId, campaignSingular }: FormattedLogSourceProps) {
  const type = source.type ?? '';
  // In-app forms resolve via the template chain (InAppFormClause), not the form list — so only
  // fetch the form list for the other form-integration types.
  const forms = useActivityLogForms(
    workspaceId,
    type === ACTIVITY_LOG_SOURCE.FormIntegration && source.form_type !== IN_APP_FORM,
  );

  const Renderer = SOURCE_RENDERERS[type];
  if (Renderer != null) {
    return (
      <Renderer
        type={type}
        log={log}
        source={source}
        workspaceId={workspaceId}
        campaignSingular={campaignSingular}
        forms={forms}
      />
    );
  }

  // Any other unrecognized type — fall back to the raw value.
  return (
    <Text size="sm" as="span">
      {type}
    </Text>
  );
}
